<template>
    <Head title="Change Logs"/>

    <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
        Change Logs
    </h1>

    <div class="bg-white py-6 px-4 mt-4 sm:p-6 shadow">
        <div class="flex justify-end">
            <search-filter :show-filter="false" v-model:value="form.search" class="w-1/4 max-w-md mr-4"
                           @reset="reset"></search-filter>
        </div>
        <div class="flex flex-col mt-3 space-y-3">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                            <tr>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Date
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Event
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Subject
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    User
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Changes
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    <span class="sr-only">View Subject</span>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-if="changelogs.data.length > 0" v-for="(log, index) in changelogs.data" :key="index"
                                :class="{'bg-gray-100': index % 2}" class="hover:bg-gray-50 cursor-pointer">
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ moment(log.created_at).format('DD-MM-YYYY HH:mm:ss') }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ log.event }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <strong>{{ log.subject_type }}:</strong> {{ log.subject_label ?? log.subject?.name ?? log.subject?.title ?? log.subject?.reference ?? log.project?.title }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ log.causer?.name ?? 'System' }}
                                </td>
                                <td class="px-6 py-4 text-sm text-gray-900">
                                    <template v-if="log.retention_summary">
                                        <p v-if="log.retention_summary.description" class="max-w-xl">
                                            {{ log.retention_summary.description }}
                                        </p>
                                        <ul v-if="Object.keys(log.retention_summary.lines || {}).length > 0" class="ml-4 mt-2 list-disc">
                                            <li v-for="(value, key) in log.retention_summary.lines" :key="key">
                                                <strong>{{ key }}:</strong> {{ value }}
                                            </li>
                                        </ul>
                                    </template>
                                    <p v-else-if="log.event === 'retention_purged' && log.description" class="max-w-xl">
                                        {{ log.description }}
                                    </p>
                                    <div v-else-if="log.properties?.attributes && Object.keys(log.properties.attributes).length > 0" class="mt-2 text-sm text-gray-700">
                                        <ul class="ml-4 mt-2 list-disc">
                                            <li v-for="(value, key, idx) in log.properties.attributes"
                                                v-show="idx < 3"
                                                :key="key">
                                                <strong v-if="key">{{ key }}:</strong>
                                                <span v-if="log.properties.old" class="text-gray-500 line-through">{{
                                                    log.properties.old[key] }}</span>
                                                    →
                                                    <span v-if="value" class="text-green-600">{{ value }}</span>
                                            </li>
                                        </ul>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <Button v-if="log.subject_url"
                                            class="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amaze-coral">
                                            <a :href="log.subject_url">
                                                View {{ log.subject_type }}
                                            </a>
                                        </Button>
                                </td>
                            </tr>
                            <tr v-else>
                                <td colspan="8" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">
                                    There are no change logs available for your search criteria.
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <Pagination :links="changelogs.links"/>
        </div>
    </div>

</template>


<script>

import Authenticated from "@/Layouts/Authenticated.vue";
import {Link, Head} from "@inertiajs/vue3";
import Pagination from "@/Components/Pagination.vue";
import SearchFilter from "@/Components/SearchFilter.vue";
import debounce from 'lodash/debounce'
import pickBy from 'lodash/pickBy'
import mapValues from 'lodash/mapValues'
import Button from "@/Components/Button.vue";
import {PencilAltIcon} from "@heroicons/vue/solid";
import moment from "moment/moment";
import { ref } from 'vue'

const expandedLogs = ref([])

// Toggle expanded state for a specific log
const toggleExpanded = (index) => {
    expandedLogs.value[index] = !expandedLogs.value[index]
}

export default {
    layout: Authenticated,
    components: {
        Button,
        Pagination,
        Head,
        Link,
        SearchFilter,
        PencilAltIcon
    },
    props: {
        changelogs: Object,
    },
    data() {
        return {
            form: {
                search: null
            }
        }
    },
    created() {
      this.moment = moment;
    },
    methods: {
        reset() {
            this.form = mapValues(this.form, () => null)
        },
    },
    watch: {
        form: {
            deep: true,
            handler: debounce(function () {
                this.$inertia.get(route('admin.change-logs.index'), pickBy(this.form), {preserveState: true})
            }, 500),
        },
    },
}
</script>
